import { useEffect } from 'react';

import type { NRecordReminders } from '@app/types';
import { createId } from '@app/utils';

import { ReminderTypes } from '@pages/Notifications/constants/reminderTypes';

import type { FormInstance } from 'antd/es/form';

type TUseNotificationsEffectsProps = {
  pageServerData: NRecordReminders.ModelBranchRecordReminders | undefined;
  form: FormInstance;
  setDefaultIntegrations: (
    integrations: NRecordReminders.ModelBranchRecordReminderIntegration[]
  ) => void;
  addInitialIntervals: () => void;
  addInitialBoxData: (
    notificationType: NRecordReminders.ReminderType,
    notificationKey: keyof NRecordReminders.ModelRecordReminder,
    formItemName: string
  ) => void;
  setNotifications: (
    notifications: (NRecordReminders.ModelRecordReminder & {
      frontId: string;
    })[]
  ) => void;
  setSendToAll: React.Dispatch<React.SetStateAction<boolean>>;
  setSendByLastActiveIntegration: React.Dispatch<React.SetStateAction<boolean>>;
};

const { Second, Create, Before, After } = ReminderTypes;

export const useRecordNotificationsEffects = ({
  pageServerData,
  form,
  setDefaultIntegrations,
  addInitialIntervals,
  addInitialBoxData,
  setNotifications,
  setSendToAll,
  setSendByLastActiveIntegration,
}: TUseNotificationsEffectsProps) => {
  useEffect(() => {
    const fetchInitialBoxData = () => {
      if (pageServerData) {
        setDefaultIntegrations(pageServerData.DefaultIntegrations || []);
        setSendToAll(pageServerData.SendByAllIntegrations);
        setSendByLastActiveIntegration(
          pageServerData.SendByLastActiveIntegration
        );

        addInitialIntervals();

        addInitialBoxData(Create, 'IsOn', 'createRecordStatus');
        addInitialBoxData(Second, 'IsOn', 'generalReminderStatus');
        addInitialBoxData(Before, 'IsOn', 'visitDayReminderStatus');
        addInitialBoxData(After, 'IsOn', 'feedbackStatus');

        addInitialBoxData(Create, 'Text', 'createRecordMessage');
        addInitialBoxData(Second, 'Text', 'generalReminderMessage');
        addInitialBoxData(Before, 'Text', 'visitDayReminderMessage');
        addInitialBoxData(After, 'Text', 'feedbackMessage');

        const SecondReminderData = pageServerData.RecordReminders?.find(
          ({ ReminderType }) => ReminderType === Second
        );
        if (SecondReminderData?.Json) {
          const parsedData = JSON.parse(SecondReminderData.Json);

          if (parsedData.IsOn) {
            const confirmByWordsText = parsedData.Words.join(', ');
            form.setFieldsValue({
              confirmByWords: parsedData.IsOn,
              confirmByWordsText: confirmByWordsText,
            });
          }
        }

        form.setFieldsValue({
          createRecordStatus:
            pageServerData.RecordReminders?.find(
              ({ ReminderType }) => ReminderType === Create
            )?.IsOn || false,
          generalReminderStatus:
            pageServerData.RecordReminders?.find(
              ({ ReminderType }) => ReminderType === Second
            )?.IsOn || false,
          visitDayReminderStatus:
            pageServerData.RecordReminders?.find(
              ({ ReminderType }) => ReminderType === Before
            )?.IsOn || false,
          feedbackStatus:
            pageServerData.RecordReminders?.find(
              ({ ReminderType }) => ReminderType === After
            )?.IsOn || false,
        });
      } else {
        form.setFieldsValue({
          createRecordStatus: false,
          generalReminderStatus: false,
          visitDayReminderStatus: false,
          feedbackStatus: false,
          createRecordMessage: '',
          generalReminderMessage: '',
          visitDayReminderMessage: '',
          feedbackMessage: '',
          confirmByWords: false,
          confirmByWordsText: '',
        });
      }
    };

    fetchInitialBoxData();
    // addInitialBoxData и addInitialIntervals в массиве зависимостей приводили к тому,
    // что при удалении интервала в блоке 2 свитчи включались (при прежде выключенном состоянии)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageServerData, form]);

  useEffect(() => {
    if (pageServerData) {
      const initialNotifications =
        pageServerData.RecordReminders?.map((item) => ({
          frontId: createId(),
          ...item,
        })) || [];

      setNotifications(initialNotifications);
    }
  }, [pageServerData, setNotifications]);
};
